/**
 * @file JSON Schema for the `workflowData` payload of the `createWorkflow` /
 * `updateWorkflow` agent tools. The bare `{ type: 'object' }` parameter let
 * models fabricate field names (e.g. `updateFormField`, `formEntityReferenceName`);
 * this schema is the real contract derived from `System.Workflow.types`, sent
 * verbatim to providers that accept standard JSON Schema (Anthropic, OpenAI).
 *
 * Drift-proofing: per-action and per-value-source schemas are keyed by the
 * `WorkflowAction['actionType']` / `WorkflowValueSource['sourceType']` /
 * `WorkflowUserParameter['type']` unions, so adding a union member fails the
 * build until its schema entry exists. The enums and `anyOf` lists are then
 * assembled from `Object.keys` / `Object.values` of those records — never
 * hand-listed. The records are runtime schema data only; no derived types.
 */
import type { WorkflowAction, WorkflowValueSource, WorkflowUserParameter } from './System.Workflow.types';

const valueSourceRef = { $ref: '#/$defs/valueSource' };

const cellValueOrSourceRef = { $ref: '#/$defs/cellValueOrSource' };

const scopeEnum = { enum: ['selectedRows', 'allRows'] };

const valueSourceSchemas: Record<WorkflowValueSource['sourceType'], Record<string, unknown>> = {
  literal: {
    type: 'object',
    additionalProperties: false,
    properties: { sourceType: { const: 'literal' }, value: {} },
    required: ['sourceType', 'value'],
  },
  nowTimestamp: {
    type: 'object',
    additionalProperties: false,
    properties: { sourceType: { const: 'nowTimestamp' } },
    required: ['sourceType'],
  },
  userParameter: {
    type: 'object',
    additionalProperties: false,
    properties: { sourceType: { const: 'userParameter' }, parameterId: { type: 'string' } },
    required: ['sourceType', 'parameterId'],
  },
  formFieldValue: {
    type: 'object',
    additionalProperties: false,
    properties: { sourceType: { const: 'formFieldValue' }, fieldId: { type: 'string' } },
    required: ['sourceType', 'fieldId'],
  },
  entityReferenceValue: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'entityReferenceValue' },
      entityOrGroupId: { type: 'string' },
      valueType: { enum: ['entityRecordId', 'entityRecordName'] },
    },
    required: ['sourceType', 'entityOrGroupId', 'valueType'],
  },
  userFieldValue: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'userFieldValue' },
      fieldId: { type: 'string' },
      valueType: { enum: ['userId', 'name', 'email'] },
      separator: { type: 'string' },
    },
    required: ['sourceType', 'fieldId', 'valueType'],
  },
  entityRecordField: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'entityRecordField' },
      entityOrGroupId: { type: 'string' },
      entityId: { type: 'string' },
      recordIdSource: valueSourceRef,
      fieldId: { type: 'string' },
    },
    required: ['sourceType', 'entityOrGroupId', 'recordIdSource', 'fieldId'],
  },
  tableAggregateSum: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'tableAggregateSum' },
      tableId: { type: 'string' },
      sumColumnId: { type: 'string' },
      rowFilters: { type: 'array', items: { $ref: '#/$defs/rowFilter' } },
    },
    required: ['sourceType', 'tableId', 'sumColumnId'],
  },
  currentUserProperty: {
    type: 'object',
    additionalProperties: false,
    properties: { sourceType: { const: 'currentUserProperty' }, property: { enum: ['email', 'name'] } },
    required: ['sourceType', 'property'],
  },
  rowCellValue: {
    type: 'object',
    additionalProperties: false,
    properties: { sourceType: { const: 'rowCellValue' }, columnId: { type: 'string' } },
    required: ['sourceType', 'columnId'],
  },
  arithmetic: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'arithmetic' },
      operator: { enum: ['add', 'subtract', 'multiply', 'divide'] },
      left: valueSourceRef,
      right: valueSourceRef,
      rounding: { enum: ['cents', 'none'] },
    },
    required: ['sourceType', 'operator', 'left', 'right'],
  },
  formProperty: {
    type: 'object',
    additionalProperties: false,
    properties: { sourceType: { const: 'formProperty' }, property: { enum: ['formNumber', 'formVersionNumber'] } },
    required: ['sourceType', 'property'],
  },
  concatenate: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'concatenate' },
      parts: { type: 'array', items: valueSourceRef },
      separator: { type: 'string' },
    },
    required: ['sourceType', 'parts'],
  },
  conditional: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'conditional' },
      condition: {
        type: 'object',
        additionalProperties: false,
        properties: {
          left: valueSourceRef,
          operator: { enum: ['==', '!=', '>=', '>', '<=', '<'] },
          right: valueSourceRef,
        },
        required: ['left', 'operator', 'right'],
      },
      trueValue: valueSourceRef,
      falseValue: valueSourceRef,
    },
    required: ['sourceType', 'condition', 'trueValue', 'falseValue'],
  },
  entityReferenceObject: {
    type: 'object',
    additionalProperties: false,
    properties: {
      sourceType: { const: 'entityReferenceObject' },
      entityOrGroupId: { type: 'string' },
      entityId: { type: 'string' },
      recordIdSource: valueSourceRef,
    },
    required: ['sourceType', 'entityOrGroupId', 'recordIdSource'],
  },
};

const actionSchemas: Record<WorkflowAction['actionType'], Record<string, unknown>> = {
  updateCells: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'updateCells' },
      targetColumn: { type: 'string' },
      value: cellValueOrSourceRef,
      scope: scopeEnum,
    },
    required: ['actionType', 'targetColumn', 'value', 'scope'],
  },
  updateTableRows: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'updateTableRows' },
      targetTableId: { type: 'string' },
      rowUpdates: {
        type: 'array',
        items: {
          type: 'object',
          additionalProperties: false,
          properties: {
            rowFilters: { type: 'array', items: { $ref: '#/$defs/rowFilter' } },
            columnUpdates: {
              type: 'array',
              items: {
                type: 'object',
                additionalProperties: false,
                properties: { columnId: { type: 'string' }, value: valueSourceRef },
                required: ['columnId', 'value'],
              },
            },
          },
          required: ['rowFilters', 'columnUpdates'],
        },
      },
    },
    required: ['actionType', 'targetTableId', 'rowUpdates'],
  },
  setFormField: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'setFormField' },
      fieldId: { type: 'string' },
      value: valueSourceRef,
    },
    required: ['actionType', 'fieldId', 'value'],
  },
  setEntityReference: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'setEntityReference' },
      entityOrGroupId: { type: 'string' },
      entityId: { type: 'string' },
      mode: { enum: ['set', 'clear'] },
      entityRecordId: { anyOf: [{ type: 'string' }, valueSourceRef] },
      entityRecordName: { type: 'string' },
    },
    required: ['actionType', 'entityOrGroupId', 'mode'],
  },
  createForm: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'createForm' },
      targetFormType: { type: 'string' },
      assignedToSource: { enum: ['currentUser', 'parameter', 'formTypeDefault'] },
      assignedToParameterId: { type: 'string' },
      linkToSource: { type: 'boolean' },
      navigateToNewForm: { type: 'boolean' },
    },
    required: ['actionType', 'targetFormType', 'assignedToSource', 'linkToSource', 'navigateToNewForm'],
  },
  copyRows: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'copyRows' },
      rowHandling: { enum: ['copy', 'reference'] },
      targetTableType: { type: 'string' },
      sourceTableType: { type: 'string' },
      scope: scopeEnum,
    },
    required: ['actionType', 'rowHandling', 'targetTableType', 'sourceTableType', 'scope'],
  },
  duplicateRows: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'duplicateRows' },
      sourceTableType: { type: 'string' },
      scope: scopeEnum,
      cellOverrides: { type: 'array', items: { $ref: '#/$defs/cellOverride' } },
      suppressFormulas: { type: 'boolean' },
    },
    required: ['actionType', 'sourceTableType', 'scope'],
  },
  syncRowsFromReferences: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'syncRowsFromReferences' },
      sourceTableType: { type: 'string' },
      sourceReferenceColumnId: { type: 'string' },
      targetTableType: { type: 'string' },
      targetReferenceColumnId: { type: 'string' },
      scope: scopeEnum,
      groupByColumn: { type: 'string' },
      groupByTargetColumn: { type: 'string' },
      cellCopies: { type: 'array', items: { $ref: '#/$defs/cellCopy' } },
    },
    required: ['actionType', 'sourceTableType', 'sourceReferenceColumnId', 'targetTableType', 'targetReferenceColumnId', 'scope'],
  },
  syncRowsByFieldValue: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'syncRowsByFieldValue' },
      sourceTableType: { type: 'string' },
      sourceColumnId: { type: 'string' },
      targetTableType: { type: 'string' },
      targetColumnId: { type: 'string' },
      scope: scopeEnum,
      cellCopies: { type: 'array', items: { $ref: '#/$defs/cellCopy' } },
    },
    required: ['actionType', 'sourceTableType', 'sourceColumnId', 'targetTableType', 'targetColumnId', 'scope'],
  },
  insertRows: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'insertRows' },
      targetTableId: { type: 'string' },
      matchColumn: { type: 'string' },
      rows: {
        type: 'array',
        items: {
          type: 'object',
          additionalProperties: false,
          properties: { cells: { type: 'object', additionalProperties: cellValueOrSourceRef } },
          required: ['cells'],
        },
      },
      deleteStale: { type: 'boolean' },
    },
    required: ['actionType', 'targetTableId', 'matchColumn', 'rows'],
  },
  setFormState: {
    type: 'object',
    additionalProperties: false,
    properties: { actionType: { const: 'setFormState' }, newState: { type: 'string' } },
    required: ['actionType', 'newState'],
  },
  lockRows: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'lockRows' },
      scope: scopeEnum,
      locked: { type: 'boolean' },
    },
    required: ['actionType', 'scope', 'locked'],
  },
  savePdfToForm: {
    type: 'object',
    additionalProperties: false,
    properties: {
      actionType: { const: 'savePdfToForm' },
      pdfVisibilityExportMode: { type: 'string' },
      visibilityParameterId: { type: 'string' },
    },
    required: ['actionType'],
  },
  send: {
    type: 'object',
    additionalProperties: true,
    properties: {
      actionType: { const: 'send' },
      targetsParameterId: { type: 'string' },
      recipientResolver: { type: 'object' },
      email: { type: 'object' },
    },
    required: ['actionType', 'targetsParameterId', 'recipientResolver', 'email'],
  },
  callSignifyApi: {
    type: 'object',
    additionalProperties: true,
    properties: {
      actionType: { const: 'callSignifyApi' },
      apiEndpoint: { type: 'string' },
    },
    required: ['actionType', 'apiEndpoint'],
  },
  callLanguageModel: {
    type: 'object',
    additionalProperties: true,
    properties: {
      actionType: { const: 'callLanguageModel' },
      prompt: valueSourceRef,
      system: { type: 'string' },
      outputStructure: {
        type: 'object',
        properties: { type: { type: 'string' }, schema: { type: 'object' } },
        required: ['type', 'schema'],
      },
      modelParams: { type: 'object' },
      responseMapping: { type: 'object' },
    },
    required: ['actionType', 'prompt', 'outputStructure'],
  },
};

const userParameterTypes: Record<WorkflowUserParameter['type'], true> = {
  userSelect: true,
  text: true,
  select: true,
  attachmentSelect: true,
  resolvedSelect: true,
  distributorSelect: true,
  number: true,
  address: true,
  date: true,
  tableColumnSelect: true,
  shippingTracker: true,
  visibilitySelect: true,
};

export const workflowDataJsonSchema: Record<string, unknown> = {
  type: 'object',
  additionalProperties: false,
  $defs: {
    description: {
      type: 'object',
      properties: { shortLabel: { type: 'string' }, longDescription: { type: 'string' } },
      required: ['shortLabel'],
    },
    valueSource: { anyOf: Object.values(valueSourceSchemas) },
    cellValueOrSource: {
      anyOf: [
        { type: 'string' },
        { type: 'number' },
        { type: 'boolean' },
        { type: 'array' },
        { type: 'null' },
        { type: 'object' },
        valueSourceRef,
      ],
    },
    rowFilter: {
      anyOf: [
        {
          type: 'object',
          additionalProperties: false,
          properties: {
            columnId: { type: 'string' },
            operator: { const: '==' },
            value: {
              anyOf: [{ type: 'string' }, { type: 'number' }, { type: 'boolean' }, { type: 'null' }, valueSourceRef],
            },
          },
          required: ['columnId', 'operator', 'value'],
        },
        {
          type: 'object',
          additionalProperties: false,
          properties: {
            columnId: { type: 'string' },
            operator: { const: 'in' },
            values: {
              type: 'array',
              minItems: 1,
              items: {
                anyOf: [{ type: 'string' }, { type: 'number' }, { type: 'boolean' }, { type: 'null' }, valueSourceRef],
              },
            },
          },
          required: ['columnId', 'operator', 'values'],
        },
      ],
    },
    cellOverride: {
      type: 'object',
      additionalProperties: false,
      properties: {
        columnId: { type: 'string' },
        value: valueSourceRef,
        validation: {
          type: 'object',
          additionalProperties: false,
          properties: { min: { type: 'number' }, max: { type: 'number' } },
        },
      },
      required: ['columnId', 'value'],
    },
    cellCopy: {
      type: 'object',
      additionalProperties: false,
      properties: {
        sourceColumnId: { type: 'string' },
        targetColumnId: { type: 'string' },
        source: { enum: ['record', 'snapshot'] },
      },
      required: ['sourceColumnId', 'targetColumnId'],
    },
  },
  properties: {
    description: { $ref: '#/$defs/description' },
    icon: { type: 'string' },
    triggerType: { enum: ['bulkRowAction', 'formAction', 'both'] },
    applicableFormTypes: { type: 'array', items: { type: 'string' } },
    applicableTableTypes: { type: 'array', items: { type: 'string' } },
    actions: { type: 'array', items: { anyOf: Object.values(actionSchemas) } },
    userParameters: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
        properties: {
          parameterId: { type: 'string' },
          label: { type: 'string' },
          required: { type: 'boolean' },
          type: { enum: Object.keys(userParameterTypes) },
        },
        required: ['parameterId', 'label', 'required', 'type'],
      },
    },
    splitScope: {
      anyOf: [
        { type: 'object', additionalProperties: false, properties: { parameterId: { type: 'string' } }, required: ['parameterId'] },
        { type: 'object', additionalProperties: false, properties: { fixedValue: { type: 'string' } }, required: ['fixedValue'] },
      ],
    },
    maxSelectedRows: { type: 'number' },
    includeHiddenRows: { type: 'boolean' },
  },
  required: ['description', 'triggerType', 'actions'],
};
